
import { computed, defineComponent, onBeforeMount, reactive } from 'vue'
import { useRouter } from 'vue-router'

// @components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import Loader from '@/common/components/UI/Loader/index.vue'

// @types
import type { NvCalendarPeriod, NvCalendarReservedPeriod } from '@/new-version/components'
import type { DispatcherSector } from '@/new-services/types/dispatcher-sectors'
import type { Period, CreatePeriodPayload } from '@/new-services/types/periods'

// @new-version-components
import { NvCalendar, NvPeriodBadge, NvSliderControls, NvSlider } from '@/new-version/components'

// @new-services
import dispatcherSectorsAPI from '@/new-services/dispatcher-sectors.service'
import periodsAPI from '@/new-services/periods.service'

// @composable
import useNotifications from '@/common/composable/useNotifications'

// @validations
import useVuelidate from '@vuelidate/core'
import { required } from '@vuelidate/validators'

type FormatReservedPeriodToCalendarResult = NvCalendarReservedPeriod & { sectors: { id: number; }[] }

export default defineComponent({
    components: {
        PageTitle,
        TheButton,
        Checkbox,
        TheInput,
        Loader,
        NvCalendar,
        NvPeriodBadge,
        NvSliderControls,
        NvSlider,
    },
    setup() {
        const router = useRouter()
        const notifications = useNotifications()

        const state = reactive({
            is_loading: false,
            is_active: true,
            name: null as string | null,
            dispatcherSectorIds: [] as Array<number>,
            period: [] as NvCalendarPeriod,
            slide: 1,
        })

        const state_form$ = useVuelidate({
            name: { required }
        }, state)

        const data_state = reactive({
            sectors: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<DispatcherSector>
            },
            reserved_periods: {
                is_loading: false,
                is_failed: false,
                list: [] as Array<NvCalendarReservedPeriod & { sectors: { id: number; }[] }>
            }
        })

        const loadSectors = async () => {
            try {
                data_state.sectors.is_loading = true

                const response = await dispatcherSectorsAPI.getDispatcherSectors({ page: 1, limit: 9999 })
                data_state.sectors.list = response.data.data

                data_state.sectors.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении секторов произошла ошибка' })
                data_state.sectors.is_failed = true
            } finally {
                data_state.sectors.is_loading = false
            }
        }

        const isSectorSelected = (sectorId: number) => {
            return state.dispatcherSectorIds.includes(sectorId)
        }

        const onSelectSector = (sectorId: number) => {
            if (data_state.reserved_periods.is_loading) return

            state.period = []

            if (isSectorSelected(sectorId)) {
                const index = state.dispatcherSectorIds.findIndex((sector_id) => sector_id === sectorId)

                if (index >= 0) {
                    state.dispatcherSectorIds.splice(index, 1)
                }

                return
            }

            state.dispatcherSectorIds.push(sectorId)
        }

        const onSelectAllSectors = () => {
            if (data_state.reserved_periods.is_loading) return

            if (isAllSectorsSelected.value) {
                state.dispatcherSectorIds = []
                return
            }

            state.dispatcherSectorIds = data_state.sectors.list.map((sector) => sector.id)
        }

        const formatReservedPeriodToCalendar = (reservedPeriod: Period): FormatReservedPeriodToCalendarResult  => {
            if (reservedPeriod.ranges[0]) {
                const dateFromParts = reservedPeriod.ranges[0].dateFrom.split('-')
                const dateToParts = reservedPeriod.ranges[0].dateTo.split('-')

                return {
                    id: reservedPeriod.id,
                    name: reservedPeriod.name,
                    dates: [
                        new Date(Number(dateFromParts[0]), Number(dateFromParts[1]) - 1, Number(dateFromParts[2]), 12),
                        new Date(Number(dateToParts[0]), Number(dateToParts[1]) - 1, Number(dateToParts[2]), 12)
                    ],
                    sectors: reservedPeriod.sectors
                }
            }

            return {
                id: reservedPeriod.id,
                name: reservedPeriod.name,
                dates: [],
                sectors: reservedPeriod.sectors
            }
        }

        const loadReservedPeriods = async () => {
            try {
                data_state.reserved_periods.is_loading = true

                const currentYear = new Date(Date.now()).getFullYear()

                const response = await periodsAPI.getPeriods({ dateFrom: formatDate(new Date(currentYear, 0, 1, 12)), dateTo: formatDate(new Date(currentYear + 1, 1, 1, 12)) })
                data_state.reserved_periods.list = response.data.data.map(formatReservedPeriodToCalendar)

                data_state.reserved_periods.is_failed = false
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При получении созданных периодов произошла ошибка' })
                data_state.reserved_periods.is_failed = true
            } finally {
                data_state.reserved_periods.is_loading = false
            }
        }

        const onPreviousSlide = () => {
            if (state.slide === 1) {
                return
            }

            state.slide = state.slide - 1
        }

        const onNextSlide = () => {
            if (state.slide === 12) {
                return
            }

            state.slide = state.slide + 1
        }

        const onResetPeriod = () => {
            state.period = []
        }

        onBeforeMount(() => {
            state.slide = new Date(Date.now()).getMonth() + 1

            loadReservedPeriods()
            loadSectors()
        })

        const formatDate = (date: Date): string => date.toISOString().split('T')[0]

        const getDateRanges = (): { fromDate: string; toDate: string; }[] => {
            return [
                {
                    fromDate: formatDate(state.period[0] as Date),
                    toDate: formatDate(state.period[1] as Date)
                }
            ]
        }

        const onSubmit = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            if (state.dispatcherSectorIds.length === 0) {
                notifications.notify({ type: 'warning', title: 'Ошибка валидации', description: 'Выберите минимум 1 сектор' })
                return
            }

            if (state.period.length < 2) {
                if (state.period.length === 1) {
                    notifications.notify({ type: 'warning', title: 'Ошибка валидации', description: 'Не указана конечная дата периода' })
                } else {
                    notifications.notify({ type: 'warning', title: 'Ошибка валидации', description: 'Не указан период' })
                }

                return
            }

            try {
                state.is_loading = true

                const payload: CreatePeriodPayload = {
                    periodName: state.name as string,
                    dispatcherSectorIds: state.dispatcherSectorIds,
                    dateRanges: getDateRanges(),
                }

                await periodsAPI.createPeriod(payload)

                notifications.notify({ type: 'success', title: 'Период создан' })
                router.push('/time-slots/periods')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'При создании периода произошла ошибка' })
            } finally {
                state.is_loading = false
            }
        }

        const isAllSectorsSelected = computed(() => data_state.sectors.list.map((sector) => sector.id).every((sectorId) => state.dispatcherSectorIds.includes(sectorId)))

        const filteredBySelectedSectorsReservedPeriods = computed(() => {
            if (data_state.reserved_periods.list.length > 0) {
                return data_state.reserved_periods.list.filter((reservedPeriod) => reservedPeriod.sectors.map((sector) => sector.id).some((sectorId) => state.dispatcherSectorIds.includes(sectorId))).filter((reservedPeriod) => reservedPeriod.dates.length >= 2)
            }

            return []
        })

        return {
            state,
            state_form$,
            data_state,
            loadSectors,
            loadReservedPeriods,
            isSectorSelected,
            onSelectSector,
            onSelectAllSectors,
            onPreviousSlide,
            onNextSlide,
            onResetPeriod,
            onSubmit,
            isAllSectorsSelected,
            filteredBySelectedSectorsReservedPeriods,
        }
    }
})
